import { API_BASE_URL, API_KEY } from '@/utils/apiConfig';

const SUPABASE_FUNCTION_URL = `${API_BASE_URL}/manage-reseller-session`;
const RESELLER_SESSION_TOKEN_KEY = 'figabl_reseller_session_token';
const RESELLER_MAGIC_LINK_MODAL_KEY = 'figabl_reseller_show_magic_link_modal';

export const markResellerMagicLinkEntry = (): void => {
  sessionStorage.setItem(RESELLER_MAGIC_LINK_MODAL_KEY, 'true');
};

export const consumeResellerMagicLinkEntry = (): boolean => {
  const shouldShow = sessionStorage.getItem(RESELLER_MAGIC_LINK_MODAL_KEY) === 'true';
  if (shouldShow) {
    sessionStorage.removeItem(RESELLER_MAGIC_LINK_MODAL_KEY);
  }
  return shouldShow;
};

const getStoredSessionToken = (): string | null =>
  sessionStorage.getItem(RESELLER_SESSION_TOKEN_KEY);

const storeSessionToken = (token: string | undefined) => {
  if (token) {
    sessionStorage.setItem(RESELLER_SESSION_TOKEN_KEY, token);
  }
};

const clearStoredSessionToken = () => {
  sessionStorage.removeItem(RESELLER_SESSION_TOKEN_KEY);
};

// Helper function to get auth headers
const getAuthHeaders = async () => {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
      'apikey': API_KEY
    };
  } catch (e) {
    // Propagate error so callers can handle according to tests
    throw e;
  }
};

export interface ResellerSessionInfo {
  valid: boolean;
  email?: string;
  organisationId?: string;
  organisationName?: string;
  expiresAt?: string;
  phoneVerified?: boolean;
  reason?: string;
}

export const setResellerSession = async (
  organisationId: string,
  email: string,
  phoneNumber?: string,
  phoneVerified?: boolean,
  submissionIdentifier?: string
): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        action: 'set',
        organisationId,
        email,
        phoneNumber,
        phoneVerified,
        submissionIdentifier
      })
    });

    if (!response.ok) {
      console.error('[resellerSession] Error setting reseller session:', response.statusText);
      return false;
    }

    const data = await response.json();
    
    if (data.error) {
      console.error('[resellerSession] Error setting reseller session:', data.error);
      return false;
    }

    // Persist token for verify when third-party cookies are blocked (cross-origin Set-Cookie)
    storeSessionToken(data.sessionToken);

    return true;
  } catch (error) {
    console.error('[resellerSession] Error setting reseller session:', error);
    return false;
  }
};

export const verifyResellerSession = async (): Promise<ResellerSessionInfo> => {
  try {
    const headers = await getAuthHeaders();

    const sessionToken = getStoredSessionToken();

    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        action: 'verify',
        ...(sessionToken ? { sessionToken } : {})
      })
    });

    if (!response.ok) {
      console.error('[resellerSession] Error verifying reseller session:', response.statusText);
      return { valid: false };
    }

    const data = await response.json();

    if (data.error) {
      console.error('[resellerSession] Error verifying reseller session:', data.error);
      return { valid: false };
    }

    return {
      valid: data.valid,
      email: data.email,
      organisationId: data.organisationId,
      organisationName: data.organisationName,
      expiresAt: data.expiresAt,
      phoneVerified: data.phoneVerified,
      reason: data.reason
    };
  } catch (error) {
    console.error('[resellerSession] Error verifying reseller session:', error);
    return { valid: false };
  }
};

export const updateResellerAccess = async (organisationId: string): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();
    const sessionToken = getStoredSessionToken();

    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        action: 'update_access',
        organisationId,
        ...(sessionToken ? { sessionToken } : {})
      })
    });

    if (!response.ok) {
      console.error('[resellerSession] Error updating reseller access:', response.statusText);
      return false;
    }

    const data = await response.json();

    if (data.error) {
      console.error('[resellerSession] Error updating reseller access:', data.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[resellerSession] Error updating reseller access:', error);
    return false;
  }
};

export const clearResellerSession = async (): Promise<boolean> => {
  try {
    const headers = await getAuthHeaders();

    const sessionToken = getStoredSessionToken();

    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        action: 'clear',
        ...(sessionToken ? { sessionToken } : {})
      })
    });

    if (!response.ok) {
      console.error('[resellerSession] Error clearing reseller session:', response.statusText);
      return false;
    }

    const data = await response.json();

    if (data.error) {
      console.error('[resellerSession] Error clearing reseller session:', data.error);
      return false;
    }

    clearStoredSessionToken();
    return true;
  } catch (error) {
    console.error('[resellerSession] Error clearing reseller session:', error);
    return false;
  }
};
